import { ArrowLeftRight, ArrowRight, Check } from 'lucide-react';

import { cn } from '@/design-system/lib/cn';
import { Avatar } from '@/design-system/ui/Avatar';
import { Button } from '@/design-system/ui/Button';
import { firstName } from '@escala/domain/palette';
import { isOpenSwap } from '@escala/domain/swaps';
import type { ScheduleEvent } from '@escala/domain/types';
import { useStore } from '@/store/AppStore';

/**
 * "Pedidos de troca" do evento, para a equipe: quem pediu → quem assume, a função e a
 * situação. Depois que o colega aceita, quem lidera a função (`editRole`) aprova ou recusa.
 */
export default function SwapRequests({ event }: { event: ScheduleEvent }) {
  const { lookup, permissions, actions } = useStore();
  const open = event.swaps.filter(isOpenSwap);

  if (!open.length) return null;

  return (
    <section
      aria-label="Pedidos de troca"
      className="flex flex-col gap-2.5 rounded-block border border-primary-line bg-primary-faint px-3.5 py-3"
    >
      <h3 className="m-0 flex items-center gap-2 text-control font-bold">
        <ArrowLeftRight aria-hidden className="size-4 stroke-[1.8] text-primary" />
        Pedidos de troca
      </h3>
      {open.map((swap) => {
        const from = lookup.members.get(swap.fromId);
        const to = lookup.members.get(swap.toId);
        const fromName = from ? firstName(from.name) : '?';
        const toName = to ? firstName(to.name) : '?';
        const waitingLeader = swap.status === 'leader';

        return (
          <div
            key={swap.id}
            className="flex flex-wrap items-center gap-2.5 rounded-control border border-primary-soft bg-white px-3 py-2.5"
          >
            <div aria-hidden className="flex items-center gap-1.5">
              <Avatar name={from?.name ?? '?'} photo={from?.photo} size="sm" />
              <ArrowRight className="size-3.5 stroke-[1.8] text-ink-placeholder" />
              <Avatar name={to?.name ?? '?'} photo={to?.photo} size="sm" />
            </div>
            <div className="min-w-40 flex-1">
              <p className="m-0 text-control font-semibold">
                {fromName} → {toName}{' '}
                <span className="font-normal text-ink-subtle">
                  · {lookup.roles.get(swap.roleId)?.role.name}
                </span>
              </p>
              <p
                className={cn('m-0 text-caption', waitingLeader ? 'text-status-partial' : 'text-ink-subtle')}
              >
                {waitingLeader
                  ? `${toName} aceitou · aguardando aprovação`
                  : `Aguardando resposta de ${toName}`}
              </p>
            </div>
            {waitingLeader && permissions.editRole(swap.roleId) ? (
              <div className="flex gap-1.5">
                <Button
                  size="xs"
                  variant="secondary"
                  onClick={() => actions.decideSwap(event.id, swap.id, false)}
                >
                  Recusar
                </Button>
                <Button size="xs" onClick={() => actions.decideSwap(event.id, swap.id, true)}>
                  <Check aria-hidden />
                  Aprovar
                </Button>
              </div>
            ) : null}
          </div>
        );
      })}
    </section>
  );
}
